/**
 * Story 061 — Components list page (`/components`).
 *
 * Mirrors `ComponentModelsPage` (Story 062) and `TurbinesPage` (Story 057)
 * shape. Columns:
 *   1. Component Code (right-aligned numeric)
 *   2. Serial Number
 *   3. Component Model Name (clickable drill-down → Story 062 modal)
 *   4. Turbine (clickable drill-down → Story 057 modal)
 *   5. Edit (pinned, pencil icon + "Edit" label per Data-page template)
 *
 *   - Filters: Search + Component Group (multi) + Show-inactive.
 *   - Column-visibility key: `aero.components.columns`.
 *   - Add button visible only for the mutate-role set
 *     (`tenant_admin | project_manager | back_office`).
 *
 * The Component Group filter renders the seven canonical
 * `global_component_groups` rows (TOWER, FOUNDATION, NACELLE,
 * SPINNER, ROTOR_BLADE, ACCESSORIES, UNDEFINED). The FE selection is
 * UUID-keyed (`?componentGroupId=<csv>`) but the BE wire still uses
 * the legacy `category=<lowercased-groupCode>` param until spec 082
 * ships native `componentGroupId` filter support. Translation lives
 * in `apps/web/src/features/component-models/shared/componentGroupMapping.ts`.
 *
 * UX brief: docs/ux/061-components-design-brief.md (forthcoming).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import type { GridColDef, GridSortModel } from "@mui/x-data-grid";
import {
  ColumnVisibilityButton,
  DataPageFooter,
  DataPageShell,
  DataPageToolbar,
  DataTable,
  DensityToggleButton,
  MultiSelectChipFilter,
  SearchField,
  sidebar as sidebarTokens,
  typography,
  useCanEditResourceFor,
  type ColumnDescriptor,
  type Density,
} from "@aeriosiq/ui";
import { useSearchParams } from "react-router-dom";
import { ToastProvider } from "../account/ToastContext.js";
import {
  useListComponentsQuery,
  type Component as ComponentRow,
} from "./queries/index.js";
import { useAllSitesQuery } from "../sites/queries/index.js";
import { InactiveBadge } from "../sites/shared/OffshoreChip.js";
import { EditTurbineDialog } from "../turbines/modal-edit/EditTurbineDialog.js";
import type { SiteOption } from "../turbines/shared/SiteAutocomplete.js";
import { EditComponentModelDialog } from "../component-models/modal-edit/EditComponentModelDialog.js";
import { AddComponentDialog } from "./modal-add/AddComponentDialog.js";
import { EditComponentDialog } from "./modal-edit/EditComponentDialog.js";
import { ComponentModelLinkCell } from "./list/ComponentModelLinkCell.js";
import { TurbineLinkCell } from "./list/TurbineLinkCell.js";
import {
  useListComponentGroupsQuery,
  type ComponentGroup,
} from "../component-models/queries/index.js";
import { findGroupNameById } from "../component-models/shared/componentGroupMapping.js";
import { PencilIcon, PlusIcon, SlidersVerticalIcon } from "@aeriosiq/ui/icons";

const COLUMNS_STORAGE_KEY = "aero.components.columns";

const COMPONENT_COLUMNS_META: ColumnDescriptor[] = [
  { field: "componentCode", headerName: "Component Code" },
  { field: "serialNumber", headerName: "Serial Number" },
  { field: "name", headerName: "Component Model Name" },
  { field: "turbine", headerName: "Turbine" },
  { field: "edit", headerName: "Edit", pinned: true },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;

type SortKey = "componentCode" | "name" | "serialNumber" | "turbine";
type SortDir = "asc" | "desc";

const SORTABLE_FIELDS: ReadonlySet<string> = new Set<string>([
  "componentCode",
  "name",
  "serialNumber",
  "turbine",
]);

function parseSort(
  value: string | null,
): { key: SortKey; dir: SortDir } | null {
  if (!value) return null;
  const [k, d] = value.split(":");
  if (!k || !d) return null;
  if (!SORTABLE_FIELDS.has(k)) return null;
  if (d !== "asc" && d !== "desc") return null;
  return { key: k as SortKey, dir: d };
}

function parsePageSize(value: string | null): number {
  const n = Number(value ?? "");
  if (
    !Number.isFinite(n) ||
    !PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number])
  ) {
    return DEFAULT_PAGE_SIZE;
  }
  return n;
}

function parseCsv(value: string | null): string[] {
  if (!value) return [];
  const seen = new Set<string>();
  for (const raw of value.split(",")) {
    const v = raw.trim();
    if (v.length > 0) seen.add(v);
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(id);
    };
  }, [value, ms]);
  return debounced;
}

// SB2-CC-04 — `MUTATE_ROLES_FOR_ADD` removed; matrix-driven
// `useCanEditResourceFor("component.create_tenant")` is the gate.

export function ComponentsPage(): ReactElement {
  return (
    <ToastProvider>
      <ComponentsPageInner />
    </ToastProvider>
  );
}

function ComponentsPageInner(): ReactElement {
  const [params, setParams] = useSearchParams();
  const componentIdInUrl = params.get("component");
  const isNewOpen = params.get("new") === "1";
  const includeInactive = params.get("includeInactive") === "1";
  const turbineIdInUrl = params.get("turbine");
  const modelIdInUrl = params.get("model");

  const [searchInput, setSearchInput] = useState(params.get("search") ?? "");
  const debouncedSearch = useDebounced(searchInput, 500);

  const [density, setDensity] = useState<Density>("compact");

  // See SitesPage for the rationale — react-router v7 setSearchParams
  // is reference-unstable on URL change; without this ref guard the
  // search-mirror effect re-fires on every navigation and strips the
  // page param via `next.delete("page")`.
  const lastAppliedSearchRef = useRef(params.get("search") ?? "");

  // Mirror search into URL on debounced change.
  useEffect(() => {
    if (lastAppliedSearchRef.current === debouncedSearch) return;
    lastAppliedSearchRef.current = debouncedSearch;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch.length > 0) {
          next.set("search", debouncedSearch);
        } else {
          next.delete("search");
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, setParams]);

  const sortParsed = parseSort(params.get("sort"));
  const pageSize = parsePageSize(params.get("pageSize"));
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);

  const componentGroupParam = params.get("componentGroupId");
  const componentGroupSelected = useMemo(
    () => parseCsv(componentGroupParam),
    [componentGroupParam],
  );

  // Component-group catalogue (7 canonical wind groups, near-static).
  // Drives the Component Group filter dropdown options.
  const { data: groupsData } = useListComponentGroupsQuery();
  const componentGroups = useMemo<ComponentGroup[]>(
    () => groupsData?.items ?? [],
    [groupsData?.items],
  );

  // R58-W-05 (assets/v1@4.0.0+): the BE accepts `componentGroupId` as
  // a CSV of UUIDs directly. No translation needed — the FE's URL
  // selection IS the wire shape. (Pre-R58-W-05 wired through a
  // `componentGroupIdsToCategoryCsv` translator that emitted the
  // legacy `category=<csv>` param; the filter chip appeared selected
  // but the BE rejected the lowercased-groupCode strings against the
  // post-4.0.0 listComponents schema, hence "filter does nothing".)

  // Track whether the `?turbine=<uuid>` param came from a click on the
  // Turbine cell in this session (the drill-down trigger). A direct
  // load of `/components?turbine=<uuid>` is also interpreted as a
  // drill-down — the param is single-purpose for this page (unlike
  // Sites/Turbines's overloaded `?site=`).
  const drillTurbineRef = useRef<string | null>(null);
  useEffect(() => {
    if (turbineIdInUrl === null && drillTurbineRef.current !== null) {
      drillTurbineRef.current = null;
    }
  }, [turbineIdInUrl]);

  const queryParams = useMemo(() => {
    const out: {
      page: number;
      pageSize: number;
      search?: string;
      sort?: string;
      includeInactive?: boolean;
      componentGroupId?: readonly string[];
    } = { page, pageSize };
    if (debouncedSearch.length > 0) out.search = debouncedSearch;
    if (sortParsed) out.sort = `${sortParsed.key}:${sortParsed.dir}`;
    if (includeInactive) out.includeInactive = true;
    if (componentGroupSelected.length > 0) {
      out.componentGroupId = componentGroupSelected;
    }
    return out;
  }, [
    page,
    pageSize,
    debouncedSearch,
    sortParsed,
    includeInactive,
    componentGroupSelected,
  ]);

  const { data, isPending, isError } = useListComponentsQuery(queryParams);

  // Sites for the Add/Edit Component modal Park picker. The modal is
  // an explicit user action — 5-min cache covers the multi-page fetch.
  const { data: sitesData } = useAllSitesQuery();
  const siteOptions = useMemo<SiteOption[]>(
    () =>
      (sitesData?.items ?? []).map((s) => ({
        id: s.id,
        siteName: s.siteName,
      })),
    [sitesData?.items],
  );

  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem(COLUMNS_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, boolean>;
      }
      return {};
    } catch {
      return {};
    }
  });

  const handleColumnVisibilityChange = useCallback(
    (next: Record<string, boolean>) => {
      setColumnVisibility(next);
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(
            COLUMNS_STORAGE_KEY,
            JSON.stringify(next),
          );
        } catch {
          // Best-effort.
        }
      }
    },
    [],
  );

  function openComponent(id: string): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("component", id);
        return next;
      },
      { replace: false },
    );
  }
  function closeComponent(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("component");
        return next;
      },
      { replace: false },
    );
  }
  function openNew(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("new", "1");
        return next;
      },
      { replace: false },
    );
  }
  function closeNew(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("new");
        return next;
      },
      { replace: false },
    );
  }
  function openTurbineDrillDown(assetId: string): void {
    drillTurbineRef.current = assetId;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("turbine", assetId);
        return next;
      },
      { replace: false },
    );
  }
  function closeTurbineDrillDown(): void {
    drillTurbineRef.current = null;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("turbine");
        return next;
      },
      { replace: false },
    );
  }
  function openComponentModelDrillDown(modelId: string): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("model", modelId);
        return next;
      },
      { replace: false },
    );
  }
  function closeComponentModelDrillDown(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("model");
        return next;
      },
      { replace: false },
    );
  }

  function setComponentGroupSelected(values: readonly string[]): void {
    const canonical = [...new Set(values.map((c) => c.trim()))]
      .filter((c) => c.length > 0)
      .sort((a, b) => a.localeCompare(b));
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        // Migrate-away from the legacy `category=<csv>` URL param so
        // a stale bookmark doesn't double-filter.
        next.delete("category");
        if (canonical.length === 0) {
          next.delete("componentGroupId");
        } else {
          next.set("componentGroupId", canonical.join(","));
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function toggleIncludeInactive(checked: boolean): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (checked) {
          next.set("includeInactive", "1");
        } else {
          next.delete("includeInactive");
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function setPage(p: number): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (p <= 1) {
          next.delete("page");
        } else {
          next.set("page", String(p));
        }
        return next;
      },
      { replace: true },
    );
  }
  function setPageSize(n: number): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (n === DEFAULT_PAGE_SIZE) {
          next.delete("pageSize");
        } else {
          next.set("pageSize", String(n));
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }

  // SB2-CC-04 — matrix-driven Add gate.
  const canAdd = useCanEditResourceFor("component.create_tenant");

  const total = data?.total ?? 0;

  const sortModel = useMemo<GridSortModel>(() => {
    if (!sortParsed) return [];
    return [{ field: sortParsed.key, sort: sortParsed.dir }];
  }, [sortParsed]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const head = model[0];
      if (!head?.sort) {
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("sort");
            return next;
          },
          { replace: true },
        );
        return;
      }
      if (!SORTABLE_FIELDS.has(head.field)) return;
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("sort", `${head.field}:${head.sort ?? "asc"}`);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const columns = useMemo<GridColDef<ComponentRow>[]>(
    () => [
      {
        field: "componentCode",
        headerName: "Component Code",
        sortable: true,
        width: 200,
        align: "left",
        headerAlign: "left",
        renderCell: (p) => (
          <Typography
            variant="body2"
            component="span"
            sx={{
              fontVariantNumeric: "tabular-nums",
              width: "100%",
              textAlign: "left",
            }}
          >
            {p.row.componentCode}
          </Typography>
        ),
      },
      {
        field: "serialNumber",
        headerName: "Serial Number",
        sortable: true,
        flex: 1.2,
        minWidth: 160,
        renderCell: (p) =>
          p.row.serialNumber ? (
            <Typography variant="body2" component="span">
              {p.row.serialNumber}
            </Typography>
          ) : (
            <Typography component="span" variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        field: "name",
        headerName: "Component Model Name",
        sortable: true,
        flex: 1.6,
        minWidth: 220,
        renderCell: (p) => (
          <Stack direction="row" alignItems="center" spacing={0}>
            <ComponentModelLinkCell
              name={p.row.assetComponentModelName}
              onOpen={() => {
                openComponentModelDrillDown(p.row.assetComponentModelId);
              }}
            />
            {!p.row.isActive ? <InactiveBadge /> : null}
          </Stack>
        ),
      },
      {
        field: "turbine",
        headerName: "Turbine",
        sortable: true,
        flex: 1.4,
        minWidth: 200,
        renderCell: (p) => (
          <TurbineLinkCell
            concatenatedName={p.row.assetConcatenatedName}
            onOpen={() => {
              openTurbineDrillDown(p.row.assetId);
            }}
          />
        ),
      },
      {
        field: "edit",
        headerName: "Edit",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: 96,
        renderCell: (p) => (
          <Button
            size="small"
            variant="text"
            startIcon={<PencilIcon fontSize="small" />}
            onClick={(e) => {
              e.stopPropagation();
              openComponent(p.row.id);
            }}
          >
            Edit
          </Button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <DataPageShell title="Components">
      <DataPageToolbar
        comfortableFilters
        ariaLabel="Components toolbar"
        filtersTestId="components-toolbar-filters"
        settingsTestId="components-toolbar-settings"
        filters={
          <>
            <SearchField
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Search"
              ariaLabel="Search"
            />
            <MultiSelectChipFilter
              label="Component Group"
              options={componentGroups.map((g) => g.id)}
              value={componentGroupSelected}
              onChange={(next) => {
                setComponentGroupSelected(next);
              }}
              optionLabel={(id) => findGroupNameById(id, componentGroups) ?? id}
              width={280}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includeInactive}
                  onChange={(_e, c) => {
                    toggleIncludeInactive(c);
                  }}
                  inputProps={{
                    "aria-label": "Show inactive components",
                  }}
                />
              }
              label="Show inactive"
              sx={{ ml: 0, mr: 0 }}
            />
          </>
        }
        settings={
          <>
            <ColumnVisibilityButton
              columns={COMPONENT_COLUMNS_META}
              model={columnVisibility}
              onChange={handleColumnVisibilityChange}
            />
            <DensityToggleButton density={density} onChange={setDensity} />
            <Box sx={{ flex: 1 }} />
            {canAdd ? (
              <Button
                aria-label="Add component"
                onClick={openNew}
                startIcon={<PlusIcon fontSize="small" />}
                disableElevation
                sx={(t) => {
                  const mode = t.palette.mode;
                  const tokens =
                    mode === "dark" ? sidebarTokens.dark : sidebarTokens.light;
                  return {
                    height: 32,
                    minHeight: 32,
                    paddingInline: 10,
                    borderRadius: 1.5,
                    fontSize: typography.size["body-md"],
                    fontWeight: typography.weight.medium,
                    letterSpacing: typography.tracking["body-md"],
                    lineHeight: 1,
                    bgcolor: tokens.selectedBg,
                    color: tokens.selectedFg,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: t.palette.primary.light,
                      boxShadow: "none",
                    },
                    "&:active": {
                      bgcolor: tokens.rowPressedBg,
                      color: tokens.selectedFg,
                      boxShadow: "none",
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${t.palette.primary.main}`,
                      outlineOffset: 2,
                    },
                    "& .MuiButton-startIcon": {
                      marginRight: 1,
                      marginLeft: -0.25,
                    },
                  };
                }}
              >
                Add component
              </Button>
            ) : null}
          </>
        }
      />

      {isPending ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            py: 6,
          }}
        >
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error" sx={{ p: 2 }}>
          Couldn&apos;t load components.
        </Typography>
      ) : data.items.length === 0 ? (
        <ComponentsEmptyState onAdd={openNew} canAdd={canAdd} />
      ) : (
        <DataTable<ComponentRow>
          rows={data.items}
          columns={columns}
          ariaLabel="Components"
          density={density}
          getRowId={(row) => row.id}
          rowCount={total}
          paginationModel={{ page: page - 1, pageSize }}
          onPaginationModelChange={(m) => {
            const nextPage = m.page + 1;
            if (m.pageSize !== pageSize) {
              setPageSize(m.pageSize);
            } else if (nextPage !== page) {
              setPage(nextPage);
            }
          }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          columnVisibilityModel={columnVisibility}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          pinnedActionColumn
          getRowClassName={(p) => (!p.row.isActive ? "row--inactive" : "")}
          sx={{
            "& .row--inactive": { opacity: 0.55 },
          }}
        />
      )}

      <DataPageFooter
        total={total}
        entityLabel="components"
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        totalTestId="components-footer-total"
      />

      <AddComponentDialog
        open={isNewOpen && canAdd}
        onClose={closeNew}
        siteOptions={siteOptions}
      />
      <EditComponentDialog
        componentId={componentIdInUrl}
        onClose={closeComponent}
        siteOptions={siteOptions}
      />
      <EditTurbineDialog
        turbineId={turbineIdInUrl}
        onClose={closeTurbineDrillDown}
        siteOptions={siteOptions}
      />
      <EditComponentModelDialog
        modelId={modelIdInUrl}
        onClose={closeComponentModelDrillDown}
      />
    </DataPageShell>
  );
}

function ComponentsEmptyState({
  onAdd,
  canAdd,
}: {
  onAdd: () => void;
  canAdd: boolean;
}): ReactElement {
  return (
    <Box
      sx={{
        flex: 1,
        py: 8,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      <SlidersVerticalIcon
        sx={{ fontSize: 48, color: "text.secondary" }}
        aria-hidden
      />
      <Typography variant="h6" color="text.primary">
        No components yet.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Add the first one.
      </Typography>
      {canAdd ? (
        <Button variant="contained" onClick={onAdd}>
          Add component
        </Button>
      ) : null}
    </Box>
  );
}
